"use client";

/**
 * Phase 52 — client-side shell that dynamically imports the
 * non-critical marketing widgets (ChatWidget) with
 * `ssr: false` so their code stays out of the initial server HTML
 * payload.
 *
 * Next.js 16 disallows `ssr: false` dynamic imports in server components,
 * so this wrapper exists solely to host those imports from a client
 * boundary while keeping the parent marketing layout a server component.
 *
 * Both components render `null` until an interaction or consent
 * event fires — no placeholder/loading UI is intentionally shown.
 */

import dynamic from "next/dynamic";
import { Toaster } from "sonner";

const ChatWidget = dynamic(() => import("@/components/chat/chat-widget"), {
  ssr: false,
});

const UtmCapture = dynamic(
  () =>
    import("@/components/analytics/utm-capture").then((m) => ({
      default: m.UtmCapture,
    })),
  { ssr: false },
);

export function MarketingDeferredWidgets() {
  return (
    <>
      <ChatWidget context="marketing" enableEscalation />
      {/* Logged-out escalation surfaces sonner toasts on submit; the marketing
          layout has no Toaster of its own, so mount one here (off the hydration
          critical path with the rest of the deferred widgets). */}
      <Toaster position="bottom-center" toastOptions={{ className: "font-sans" }} richColors />
    </>
  );
}

// UtmCapture is a separate wrapper because the marketing layout mounts it
// inside the PostHogProvider tree (not after it). UtmCapture returns null and
// reads URL params + writes localStorage in useEffect — safe to defer past the
// hydration critical path. Removes posthog-js as an eager transitive import
// from the marketing route trees.
export function MarketingUtmCapture() {
  return <UtmCapture />;
}
